/**
 * Shared constants for the Social Media Control Center (SMCC)
 */

// Platform types
export type SMCCPlatformId =
  | 'instagram'
  | 'tiktok'
  | 'twitter'
  | 'facebook'
  | 'pinterest'
  | 'youtube'
  | 'linkedin';

export interface PlatformConfig {
  id: SMCCPlatformId;
  name: string;
  color: string;
  bgColor: string;
  maxLength: number;
  maxHashtags: number;
  supportsMedia: boolean;
  maxImages: number;
  maxVideos: number;
}

/**
 * Platform configuration with all properties unified from across SMCC components
 */
export const PLATFORM_CONFIG: Record<SMCCPlatformId, PlatformConfig> = {
  instagram: {
    id: 'instagram',
    name: 'Instagram',
    color: '#E4405F',
    bgColor: 'bg-pink-50',
    maxLength: 2200,
    maxHashtags: 30,
    supportsMedia: true,
    maxImages: 10,
    maxVideos: 1,
  },
  tiktok: {
    id: 'tiktok',
    name: 'TikTok',
    color: '#000000',
    bgColor: 'bg-gray-50',
    maxLength: 2200,
    maxHashtags: 30,
    supportsMedia: true,
    maxImages: 0,
    maxVideos: 1,
  },
  twitter: {
    id: 'twitter',
    name: 'X (Twitter)',
    color: '#1DA1F2',
    bgColor: 'bg-blue-50',
    maxLength: 280,
    maxHashtags: 5,
    supportsMedia: true,
    maxImages: 4,
    maxVideos: 1,
  },
  facebook: {
    id: 'facebook',
    name: 'Facebook',
    color: '#1877F2',
    bgColor: 'bg-blue-50',
    maxLength: 63206,
    maxHashtags: 10,
    supportsMedia: true,
    maxImages: 10,
    maxVideos: 1,
  },
  pinterest: {
    id: 'pinterest',
    name: 'Pinterest',
    color: '#BD081C',
    bgColor: 'bg-red-50',
    maxLength: 500,
    maxHashtags: 0,
    supportsMedia: true,
    maxImages: 5,
    maxVideos: 0,
  },
  youtube: {
    id: 'youtube',
    name: 'YouTube',
    color: '#FF0000',
    bgColor: 'bg-red-50',
    maxLength: 5000,
    maxHashtags: 15,
    supportsMedia: true,
    maxImages: 0,
    maxVideos: 1,
  },
  linkedin: {
    id: 'linkedin',
    name: 'LinkedIn',
    color: '#0A66C2',
    bgColor: 'bg-blue-50',
    maxLength: 3000,
    maxHashtags: 5,
    supportsMedia: true,
    maxImages: 9,
    maxVideos: 1,
  },
};

/**
 * Get all platform IDs as an array
 */
export const PLATFORM_IDS = Object.keys(PLATFORM_CONFIG) as SMCCPlatformId[];

/**
 * Content types for SMCC posts
 */
export const CONTENT_TYPES = [
  { value: 'post', label: 'Post', icon: 'image' },
  { value: 'story', label: 'Story', icon: 'clock' },
  { value: 'reel', label: 'Reel', icon: 'video' },
  { value: 'video', label: 'Video', icon: 'video' },
  { value: 'carousel', label: 'Carousel', icon: 'images' },
] as const;

export type ContentType = typeof CONTENT_TYPES[number]['value'];

/**
 * Automation levels for content publishing
 */
export const AUTOMATION_LEVELS = [
  {
    value: 'manual',
    label: 'Manual',
    description: 'Publish manually when ready'
  },
  {
    value: 'human_approval',
    label: 'Requires Approval',
    description: 'Needs human approval before publishing'
  },
  {
    value: 'fully_automated',
    label: 'Automated',
    description: 'Publish automatically when scheduled'
  },
] as const;

export type AutomationLevel = typeof AUTOMATION_LEVELS[number]['value'];

/**
 * Content status values
 */
export const CONTENT_STATUSES = [
  { value: 'draft', label: 'Draft', color: 'gray' },
  { value: 'pending_approval', label: 'Pending Approval', color: 'amber' },
  { value: 'approved', label: 'Approved', color: 'blue' },
  { value: 'scheduled', label: 'Scheduled', color: 'purple' },
  { value: 'publishing', label: 'Publishing', color: 'indigo' },
  { value: 'published', label: 'Published', color: 'green' },
  { value: 'failed', label: 'Failed', color: 'red' },
  { value: 'archived', label: 'Archived', color: 'gray' },
  { value: 'rejected', label: 'Rejected', color: 'red' },
] as const;

export type ContentStatus = typeof CONTENT_STATUSES[number]['value'];

/**
 * Account status values
 */
export const ACCOUNT_STATUSES = [
  { value: 'active', label: 'Active', color: 'green' },
  { value: 'disconnected', label: 'Disconnected', color: 'gray' },
  { value: 'expired', label: 'Token Expired', color: 'amber' },
  { value: 'suspended', label: 'Suspended', color: 'red' },
  { value: 'pending', label: 'Pending', color: 'blue' },
] as const;

export type AccountStatus = typeof ACCOUNT_STATUSES[number]['value'];

/**
 * Analytics period options
 */
export const ANALYTICS_PERIODS = [
  { value: '24h', label: 'Last 24 Hours', hours: 24 },
  { value: '7d', label: 'Last 7 Days', hours: 24 * 7 },
  { value: '30d', label: 'Last 30 Days', hours: 24 * 30 },
  { value: '90d', label: 'Last 90 Days', hours: 24 * 90 },
] as const;

export type AnalyticsPeriod = typeof ANALYTICS_PERIODS[number]['value'];

/**
 * Get status badge colors for Tailwind classes
 */
export function getStatusColors(status: ContentStatus): { bg: string; text: string } {
  const colorMap: Record<ContentStatus, { bg: string; text: string }> = {
    draft: { bg: 'bg-gray-100', text: 'text-gray-700' },
    pending_approval: { bg: 'bg-amber-100', text: 'text-amber-700' },
    approved: { bg: 'bg-blue-100', text: 'text-blue-700' },
    scheduled: { bg: 'bg-purple-100', text: 'text-purple-700' },
    publishing: { bg: 'bg-indigo-100', text: 'text-indigo-700' },
    published: { bg: 'bg-green-100', text: 'text-green-700' },
    failed: { bg: 'bg-red-100', text: 'text-red-700' },
    archived: { bg: 'bg-gray-100', text: 'text-gray-700' },
    rejected: { bg: 'bg-red-100', text: 'text-red-700' },
  };
  return colorMap[status] || { bg: 'bg-gray-100', text: 'text-gray-700' };
}

/**
 * Get account status badge colors
 */
export function getAccountStatusColors(status: AccountStatus): { bg: string; text: string } {
  const colorMap: Record<AccountStatus, { bg: string; text: string }> = {
    active: { bg: 'bg-green-100', text: 'text-green-700' },
    disconnected: { bg: 'bg-gray-100', text: 'text-gray-700' },
    expired: { bg: 'bg-amber-100', text: 'text-amber-700' },
    suspended: { bg: 'bg-red-100', text: 'text-red-700' },
    pending: { bg: 'bg-blue-100', text: 'text-blue-700' },
  };
  return colorMap[status] || { bg: 'bg-gray-100', text: 'text-gray-700' };
}
